import styled from "@emotion/styled";
import { useCallback, useMemo, type CSSProperties } from "react";
import { useSelector } from "react-redux";

import {
  Avatar,
  Icon,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  ModalDialog,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import type { CreateNode } from "../nodes";
import { applyDefinitionSelector, createNodeSelector, useMutateNodeDesigns } from "../state/index.ts";
import type { CreateableNode, DesignerNode } from "../types";
import type { SelectorOptions } from "./shared";
import { useFindDefinitions } from "./useFindDefinitions";

const Content = styled.div`
  width: 480px;
  height: 60vh;
`;

const paperStyle: CSSProperties = { maxHeight: "100%", overflow: "hidden" };

interface Props {
  selectorOptions: SelectorOptions;
  onSelected: (newNode: DesignerNode) => void;
  onClose: () => void;
}

export const DefinitionBrowser = ({ selectorOptions, onSelected, onClose }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { add, replace } = useMutateNodeDesigns();
  const applyDefinition = useSelector(applyDefinitionSelector);
  const createNode = useSelector(createNodeSelector);

  const { fetchState, definitions } = useFindDefinitions(selectorOptions.criteria);

  const handlePopulateSlot = useCallback(
    (newNode: ReturnType<CreateNode>) => {
      if (!selectorOptions) return;

      const { target } = selectorOptions;

      if ("slotId" in target) {
        const { parentId, name } = target.slotId;
        add(newNode, { parentId, name });
      } else if ("slotResidentNodeId" in target) {
        replace(target.slotResidentNodeId, newNode);
      } else {
        throw new Error(`target type ${JSON.stringify(target)} not supported here.`);
      }

      onSelected(newNode);
    },
    [add, onSelected, replace, selectorOptions],
  );

  const handleCreatableSelected = useCallback(
    ({ typeId, definitionId }: CreateableNode) => {
      // NOTE/TODO: if a node designer has parameters we don't have support for that (yet?), ideally they are optional
      let newNode = createNode(typeId, {});

      if (definitionId) {
        newNode = applyDefinition(newNode, definitionId);
      }

      handlePopulateSlot(newNode);
    },
    [applyDefinition, createNode, handlePopulateSlot],
  );

  const sortedDefinitions = useMemo(() => {
    // Sort definitions so if typeId is composableActivity or group, they are at the top. Otherwise sort by title.
    return definitions.sort((a, b) => {
      if (a.typeId === "composableActivity" || a.typeId === "navGroup") return -1;
      if (b.typeId === "composableActivity" || b.typeId === "navGroup") return 1;
      return a.title.localeCompare(b.title);
    });
  }, [definitions]);

  if (fetchState === "pending") return <p>{formatMessage("loading")}</p>;

  return (
    <ModalDialog
      open={!!selectorOptions}
      onClose={onClose}
      header={formatMessage("title")}
      buttons="showClose"
      slotProps={{ paper: { style: paperStyle } }}
    >
      <Content>
        {definitions.length === 0 && <p>{formatMessage("noMatches")}</p>}

        {definitions.length && (
          <List data-test="list">
            {sortedDefinitions.map((d) => (
              <ListItem key={d.definitionId ?? d.typeId} disablePadding data-test="item">
                <ListItemButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCreatableSelected(d);
                  }}
                  data-test="button"
                >
                  <ListItemAvatar data-test="avatar-container">
                    <Avatar>
                      <Icon icon="object-group" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText data-test="title">{d.title}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Content>
    </ModalDialog>
  );
};

const formatStrings = {
  loading: {
    en: "Loading...",
  },
  noMatches: {
    en: "No matches found.",
  },
  results: {
    en: "Results",
  },
  title: {
    en: "Choose Slot Inhabitant",
  },
} satisfies FormatMessages;
